import { BehaviorSubject } from 'rxjs';
import { v1, toResult } from '../api/v1/client';
import { currentUserId } from '../clients/BackendClient';
import type { components } from '@/generated/contracts/api-v1';
import type { Notification, NotificationService } from '../../modules/notificationService';

// The bell shows the newest page only, as many as the old route's default
const LIST_LIMIT = 20;

// The unread dot is how a new notification shows up, so a visible tab re-reads at this pace
const REFRESH_INTERVAL_MS = 60_000;

export class NotificationServiceAdapter implements NotificationService {
  private subject = new BehaviorSubject<Notification[]>([]);
  private timer: ReturnType<typeof setInterval> | null = null;
  // The user the shown list belongs to; another user signing in in this tab starts from an empty list
  private ownerUserId: string | null = null;
  private readSeq = 0;

  public notifications$ = this.subject.asObservable();

  async connect(): Promise<void> {
    this.claimForCurrentUser();
    if (!this.timer) {
      this.timer = setInterval(() => {
        if (!document.hidden) void this.refresh();
      }, REFRESH_INTERVAL_MS);
    }
    await this.refresh();
  }

  async disconnect(): Promise<void> {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.ownerUserId = null;
    this.readSeq += 1;
    this.subject.next([]);
  }

  async refresh(): Promise<void> {
    const userId = this.claimForCurrentUser();
    if (!userId || !this.getAccessToken()) return;
    const seq = ++this.readSeq;
    const rows = await this.fetchAll();
    // A read that started before a newer one, or under another user, is dropped
    if (rows && seq === this.readSeq && currentUserId() === userId) this.subject.next(rows);
  }

  /** Throws when the server refused, after putting the dot back. */
  async markAsRead(id: number): Promise<void> {
    const wasRead = this.subject.value.find(n => n.id === id)?.read ?? false;
    this.setRead(n => n.id === id, true);
    const { error } = await toResult(v1.POST('/api/v1/notifications/{notificationId}/read', { params: { path: { notificationId: id } } }));
    if (error) {
      this.setRead(n => n.id === id, wasRead);
      throw error;
    }
    await this.refresh();
  }

  /** Throws when the server refused, after putting the dots back. */
  async markAllAsRead(): Promise<void> {
    const unread = new Set(this.subject.value.filter(n => !n.read).map(n => n.id));
    this.setRead(() => true, true);
    const { error } = await toResult(v1.POST('/api/v1/notifications/read-all'));
    if (error) {
      this.setRead(n => unread.has(n.id), false);
      throw error;
    }
    await this.refresh();
  }

  // A read already in flight predates this change, so it is dropped rather than undoing it
  private setRead(match: (n: Notification) => boolean, read: boolean) {
    this.readSeq += 1;
    this.subject.next(this.subject.value.map(n => (match(n) ? { ...n, read } : n)));
  }

  private claimForCurrentUser(): string | null {
    const userId = currentUserId();
    if (userId !== this.ownerUserId) {
      this.ownerUserId = userId;
      this.readSeq += 1;
      if (this.subject.value.length) this.subject.next([]);
    }
    return userId;
  }

  // Null when the read failed, so a blip keeps the list already shown
  private async fetchAll(): Promise<Notification[] | null> {
    const { data } = await toResult(v1.GET('/api/v1/notifications', { params: { query: { limit: LIST_LIMIT } } }));
    return data ? data.items.map(r => this.toNotification(r)) : null;
  }

  private toNotification(r: components['schemas']['NotificationDto']): Notification {
    return {
      id: r.id,
      typeKey: r.typeKey,
      subject: r.subject,
      message: r.message,
      sceneInstanceId: r.sceneUniqueId ?? null,
      read: r.read,
      createdAt: r.createdAt,
    };
  }

  private getAccessToken(): string | null {
    try {
      const raw = localStorage.getItem('lureo_session');
      if (!raw) return null;
      const session = JSON.parse(raw);
      return session?.access_token ?? null;
    } catch {
      return null;
    }
  }
}
